import { useEffect, useRef, useState } from 'react'
import { Button, Card, Text } from '@cottonray/ui-web'
import {
  EMAIL_MAX_LENGTH,
  MAX_PHOTOS,
  MAX_PHOTO_BYTES,
  MESSAGE_MAX_LENGTH,
  MESSAGE_MIN_LENGTH,
  type SubmitInquiryRequest,
  type SupportCategory,
} from '@cottonray/support-contract'
import { findPolicy } from './policies'
import { fill, resolveLabels, type SupportLabels } from './support/labels'
import { accentOverride, type SupportContext } from './support/params'
import { preparePhoto, type PreparedPhoto } from './support/photos'
import { submitInquiry, type SubmitFailure } from './support/submit'

/**
 * 공용 지원 문의 화면 — **전 서비스가 이 한 페이지를 연다** (2026-08-26 사용자 지시:
 * "문의하기를 웹사이트로 배포하고. cottonray.com 에 하나로").
 *
 * 앱마다 있던 네이티브 화면(`@cottonray/support-native` 의 `SupportScreen`)을 대신한다.
 * 옮긴 이유는 **문구 한 줄·칸 하나를 고치려고 앱 일곱 개를 다시 심사에 올리는 구조**를
 * 없애기 위해서다. 이제 문의 화면의 변경은 사이트 배포로 끝난다.
 *
 * 앱은 자기 테마·환경·회신 주소를 주소에 실어 이 페이지를 연다 (`support/params.ts`).
 * 그래서 앱에서 열어도 앱의 색이 그대로고, 문의를 읽는 쪽이 보는 정보도 그대로다.
 */
export interface SupportInquiryProps {
  context: SupportContext
}

/** 모양만 본다 — 정규식으로 RFC 를 흉내 내면 멀쩡한 주소를 막는 쪽으로 틀린다 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const ERROR_LABEL: Record<SubmitFailure['reason'], (labels: SupportLabels) => string> = {
  rateLimited: (labels) => labels.errorRateLimited,
  invalid: (labels) => labels.errorInvalid,
  tooLarge: (labels) => labels.errorTooLarge,
  network: (labels) => labels.errorNetwork,
  server: (labels) => labels.errorServer,
}

type Status = 'idle' | 'sending' | 'sent'

export function SupportInquiry({ context }: SupportInquiryProps) {
  const labels = resolveLabels(context.language)
  const [category, setCategory] = useState<SupportCategory>(context.categories[0] ?? 'ETC')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState(context.email ?? '')
  const [photos, setPhotos] = useState<PreparedPhoto[]>([])
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | undefined>(undefined)
  const [receipt, setReceipt] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)

  // 회신 주소는 프래그먼트로 왔다 — 값을 state 로 옮겼으니 **주소에서는 지운다.**
  // 주소창을 캡처해 문의에 붙여도 메일 주소가 딸려 가지 않게 하기 위해서다
  useEffect(() => {
    if (window.location.hash !== '') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }, [])

  // 앱이 여는 입구지 검색에서 찾아올 페이지가 아니다. sitemap 에도 없다(seo.ts)
  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex'
    document.head.appendChild(meta)
    document.documentElement.lang = context.language
    return () => meta.remove()
  }, [context.language])

  // 미리보기는 blob URL 이라 화면을 떠날 때 돌려줘야 한다
  useEffect(() => () => photos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl)), [photos])

  const policy = findPolicy(context.service)

  async function pick(files: FileList | null): Promise<void> {
    if (files === null) return
    const remaining = MAX_PHOTOS - photos.length
    const accepted: PreparedPhoto[] = []
    let rejection: 'type' | 'size' | undefined

    for (const file of [...files].slice(0, remaining)) {
      const prepared = await preparePhoto(file)
      if (prepared === 'type' || prepared === 'size') rejection = prepared
      else accepted.push(prepared)
    }

    if (accepted.length > 0) setPhotos((current) => [...current, ...accepted])
    setError(
      rejection === 'type'
        ? labels.photoRejectedType
        : rejection === 'size'
          ? fill(labels.photoRejectedSize, { max: Math.round(MAX_PHOTO_BYTES / 1024 / 1024) })
          : undefined,
    )
  }

  function remove(id: string): void {
    setPhotos((current) => {
      const gone = current.find((photo) => photo.id === id)
      if (gone !== undefined) URL.revokeObjectURL(gone.previewUrl)
      return current.filter((photo) => photo.id !== id)
    })
  }

  async function send(): Promise<void> {
    const trimmed = message.trim()
    if (trimmed.length < MESSAGE_MIN_LENGTH) {
      setError(fill(labels.messageTooShort, { min: MESSAGE_MIN_LENGTH }))
      return
    }
    const contact = email.trim()
    if (contact !== '' && !EMAIL_SHAPE.test(contact)) {
      setError(labels.emailInvalid)
      return
    }

    setStatus('sending')
    setError(undefined)

    const request: SubmitInquiryRequest = {
      service: context.service,
      platform: context.platform,
      category,
      message: trimmed,
      ...(contact === '' ? {} : { contactEmail: contact }),
      ...(context.appVersion === undefined ? {} : { appVersion: context.appVersion }),
      ...(context.osVersion === undefined ? {} : { osVersion: context.osVersion }),
      ...(context.deviceModel === undefined ? {} : { deviceModel: context.deviceModel }),
      ...(context.locale === undefined ? {} : { locale: context.locale }),
    }

    const result = await submitInquiry(request, photos)
    if (result.ok) {
      setReceipt(result.id.slice(0, 8))
      setStatus('sent')
      return
    }
    setStatus('idle')
    setError(ERROR_LABEL[result.reason](labels))
  }

  const shell = context.embed ? 'support-form support-form--embed' : 'support-form'

  if (status === 'sent') {
    return (
      <main className={shell}>
        {context.accentHex !== undefined && <style>{accentOverride(context.accentHex)}</style>}
        <Card raised className="support-form__done">
          <Text variant="title">{labels.sentTitle}</Text>
          <Text tone="muted">{labels.sentBody}</Text>
          <Text variant="label" tone="subtle" className="support-form__receipt">
            {fill(labels.receipt, { id: receipt })}
          </Text>
          {/* 웹뷰 안에는 닫을 창이 없다 — 돌아가는 길은 앱의 뒤로가기다 */}
          {!context.embed && (
            <Text tone="subtle" variant="caption">
              {labels.closeHint}
            </Text>
          )}
        </Card>
      </main>
    )
  }

  const sending = status === 'sending'

  return (
    <main className={shell}>
      {context.accentHex !== undefined && <style>{accentOverride(context.accentHex)}</style>}

      <header className="support-form__head">
        {/* 폰에서 여는 폼이라 display(48px)는 화면 한 판을 제목이 먹는다.
            태그는 h1 로 두고 크기만 title 로 내린다.
            웹뷰 안에서는 아예 안 그린다 — 앱 상단바에 같은 말이 이미 있다 */}
        {!context.embed && (
          <Text as="h1" variant="title">
            {labels.title}
          </Text>
        )}
        <Text tone="muted">{labels.intro}</Text>
      </header>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          void send()
        }}
      >
        <fieldset className="support-form__field support-form__topics">
          <legend>
            <Text as="span" variant="label">
              {labels.categoryLabel}
            </Text>
          </legend>
          <div className="support-form__chips">
            {context.categories.map((name) => (
              <label
                key={name}
                className={`support-chip${category === name ? ' support-chip--on' : ''}`}
              >
                <input
                  type="radio"
                  name="category"
                  value={name}
                  checked={category === name}
                  onChange={() => setCategory(name)}
                />
                {labels.categoryName[name]}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="support-form__field">
          <label htmlFor="support-message">
            <Text as="span" variant="label">
              {labels.messageLabel}
            </Text>
          </label>
          <textarea
            id="support-message"
            className="support-form__textarea"
            value={message}
            onChange={(event) => setMessage(event.target.value.slice(0, MESSAGE_MAX_LENGTH))}
            placeholder={labels.messagePlaceholder}
            rows={7}
            required
          />
          <Text variant="caption" tone="subtle">
            {fill(labels.messageCounter, { count: message.length, max: MESSAGE_MAX_LENGTH })}
          </Text>
        </div>

        <div className="support-form__field">
          <Text as="span" variant="label">
            {labels.photoLabel}
          </Text>
          <div className="support-form__photos">
            {photos.map((photo) => (
              <div key={photo.id} className="support-photo">
                <img src={photo.previewUrl} alt="" />
                <button
                  type="button"
                  className="support-photo__remove"
                  aria-label={labels.photoRemove}
                  onClick={() => remove(photo.id)}
                >
                  ×
                </button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <Button variant="secondary" onClick={() => fileInput.current?.click()}>
                {labels.photoAdd}
              </Button>
            )}
          </div>
          <input
            ref={fileInput}
            type="file"
            // **`image/*` 로 받는다** — jpg·png·webp 로 좁히면 아이폰의 HEIC 사진이 목록에서
            // 회색으로 죽는다. 브라우저가 고르는 단계에서 JPEG 로 바꿔 주고, 그래도 남는 것은
            // `preparePhoto` 가 캔버스로 다시 그려 맞춘다
            accept="image/*"
            multiple
            hidden
            onChange={(event) => {
              void pick(event.target.files)
              // 같은 파일을 다시 고를 수 있어야 한다 — 값이 남아 있으면 change 가 안 뜬다
              event.target.value = ''
            }}
          />
          <Text variant="caption" tone="subtle">
            {fill(labels.photoCounter, { count: photos.length, max: MAX_PHOTOS })}
          </Text>
          <Text variant="caption" tone="muted">
            {labels.photoPrivacyNote}
          </Text>
        </div>

        <div className="support-form__field">
          <label htmlFor="support-email">
            <Text as="span" variant="label">
              {labels.emailLabel}
            </Text>
          </label>
          <input
            id="support-email"
            className="support-form__input"
            type="email"
            inputMode="email"
            autoComplete="email"
            maxLength={EMAIL_MAX_LENGTH}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={labels.emailPlaceholder}
          />
          <Text variant="caption" tone="muted">
            {labels.emailHelper}
          </Text>
        </div>

        <Text variant="caption" tone="muted" className="support-form__note">
          {context.fromApp ? labels.privacyNote : labels.privacyNoteWeb}
          {policy !== undefined && (
            <>
              {' '}
              <a href={`/privacy/${context.service}`}>{labels.privacyLink}</a>
            </>
          )}
        </Text>

        {error !== undefined && (
          <Text tone="danger" role="alert" className="support-form__error">
            {error}
          </Text>
        )}

        <Button type="submit" size="lg" fullWidth disabled={sending}>
          {sending ? labels.sending : labels.submit}
        </Button>
      </form>
    </main>
  )
}
